import { Injectable, inject, signal } from '@angular/core';
import { ApiService, type MemberRole } from './api.service';

// orgId는 "{shard}:{id}" 포맷(예: "0:143457") — 백엔드 OrgDto.id/MyOrgDto.id와 동일 컨벤션.
// teams는 `MyOrgDto.teams`(MyOrgTeamContextDto[])에서 표시에 필요한 teamId/teamName만 옮긴다
// (2026-08-18, context switcher team 표시 — opt-in `teamsEnabled`). teamId도 동일한
// "{shard}:{id}" 포맷이다(2026-08-20 값공간 통일 — 이전엔 bare number).
//
// role은 `MyOrgDto.role` — org-root에 직접 소속된 자리의 역할이고, team으로만 연결된 사용자(team-only)는
// null이다(서버 my-org.dto.ts 참고). undefined는 "아직 모른다"(방금 만든 단체 등)라 null과 구분한다.
export type Org = {
  orgId: string;
  name: string;
  role?: MemberRole | null;
  teams?: { teamId: string; teamName: string; role?: MemberRole | null }[];
};

/**
 * "team으로만 연결된, 단체 관리 영역에 들어올 이유가 없는 사용자"인가 — org 직속 role이 없고(null) 어느
 * 팀에서도 팀 관리 역할(Owner/Admin)이 없을 때다. 팀 admin은 서버가 위임한 팀 관리 화면(학급구조·학생
 * 명단)이 있어 여기 포함하지 않는다. org를 모르거나 role이 undefined(아직 모름)이면 false — UI 판정은
 * fail-open이고 실제 인가는 API가 한다. `orgAdminAreaGuard`와 팀 뷰 사이드바가 이 함수 하나를 공유한다.
 */
export function isTeamOnlyOrg(org: Org | undefined): boolean {
  if (!org || org.role !== null) return false;
  return !(org.teams ?? []).some((t) => t.role === 'Owner' || t.role === 'Admin');
}

/**
 * 팀 관리 화면(서비스 설정 등)을 사이드바에 낼 만한 사용자인가 — org 직속 Owner/Admin이거나 어느 팀에서든 Owner/Admin일 때다.
 * 서버 쓰기 가드는 이 팀의 관리자·상위 팀 관리자·단체 관리자를 통과시키는데, 사이드바 UI 판정은 그보다 넓게(fail-open) 본다 —
 * org를 모르면 true. 실제 인가는 API가 하므로 팀 설정을 못 쓰는 사용자가 메뉴를 눌러 403 안내를 볼 수는 있다.
 */
export function canManageTeamSettings(org: Org | undefined): boolean {
  if (!org) return true;
  if (org.role === 'Owner' || org.role === 'Admin') return true;
  return (org.teams ?? []).some((t) => t.role === 'Owner' || t.role === 'Admin');
}

/**
 * 로그인 사용자의 단체 목록 + 현재 선택된 단체를 앱 전역에서 공유한다.
 * app-shell의 헤더 드롭다운과 각 페이지(예: consent-list의 "단체 선택")가 같은 상태를 본다.
 */
@Injectable({ providedIn: 'root' })
export class OrgContextService {
  private readonly api = inject(ApiService);

  private readonly _orgs = signal<Org[]>([]);
  private readonly _selectedOrgId = signal<string | null>(null);
  private loadStarted = false;
  // 목록 조회가 끝났는지(성공·실패 모두) — 라우트 가드가 role을 보기 전에 기다린다.
  private readonly _loaded = signal(false);
  // reset() 이후 도착하는 이전 사용자의 늦은 응답을 버린다 — 로그아웃 직후 다른 사용자가 로그인해도 안전하게.
  private generation = 0;

  readonly orgs = this._orgs.asReadonly();
  readonly loaded = this._loaded.asReadonly();
  readonly selectedOrgId = this._selectedOrgId.asReadonly();

  ensureLoaded(): void {
    if (this.loadStarted) return;
    this.loadStarted = true;
    const generation = this.generation;
    this.api.getMyOrgs().subscribe({
      next: (res) => {
        if (generation !== this.generation) return;
        this._orgs.set(
          res.items.map((o) => ({ orgId: o.id, name: o.name, role: o.role ?? null, teams: o.teams })),
        );
        this._loaded.set(true);
      },
      error: () => {
        if (generation !== this.generation) return;
        this.loadStarted = false;
        // 실패해도 완료로 본다 — 가드가 무한 대기하지 않게(UI 가드는 fail-open, 서버가 실제로 막는다).
        this._loaded.set(true);
      },
    });
  }

  selectOrg(orgId: string): void {
    this._selectedOrgId.set(orgId);
  }

  // 새로 개설한 단체를 목록 재조회 없이 바로 반영하고 활성 컨텍스트로 전환한다.
  addOrg(org: Org): void {
    this._orgs.update((list) => [...list, org]);
    this._selectedOrgId.set(org.orgId);
  }

  // 로그아웃 시 반드시 호출한다 — root-singleton이라 페이지 새로고침 없이 다른 사용자가
  // 로그인하면 loadStarted가 그대로 남아 ensureLoaded()가 no-op이 되고, 이전 사용자의
  // org 목록·선택 상태가 새 사용자에게 그대로 노출된다(2026-07-06 코드 리뷰에서 발견).
  reset(): void {
    this._orgs.set([]);
    this._selectedOrgId.set(null);
    this._loaded.set(false);
    this.loadStarted = false;
    this.generation++;
  }
}
